<template>
  <div class="auth">
    <div class="login-form">
      <div class="form-left">
        <div class="mb-10">
          <img alt="BeMob" src="/images/logo-white2.svg" />
        </div>
        <p class="mb-32 pr-30">
          {{
            t(
              'BeMob helps to manage your ad campaigns, identify the most profitable traffic sources, measure your landing pages performance and provide highly detailed real-time reports.',
            )
          }}
        </p>
        <div class="login-signup">
          <ui-button :to="{ path: 'logout' }" :text="t('Logout')" icon="fas fa-sign-out-alt" />
        </div>
        <div class="copyright">
          2015-{{ moment().year() }}
          {{ t('© BeMob. All rights reserved.') }}
        </div>
      </div>

      <div class="form-right">
        <div class="form-right-inner">
          <div class="login-title">{{ rightSideTitle }}</div>

          <div class="error-block mb-22 d-flex align-center justify-start" v-if="apiErrors">
            <div class="error-text">
              <ui-icon size="12" class="mr-8" icon="fas fa-exclamation-triangle" />
              {{ apiErrors }}
            </div>
          </div>

          <div v-if="twoFactorQrCode" class="two-factor-qr mb-14">
            <div class="mb-1">
              {{ t('1. Open Google Authenticator and scan QR-code') }}
            </div>
            <div class="form-apps">
              <img class="form-apps-img mx-1" src="/images/appstore-button.png" />
              <img class="form-apps-img mx-1" src="/images/google-play-button.png" />
            </div>
            <div class="form-qr-code">
              <img width="100%" :src="twoFactorQrCode" />
            </div>
            <div class="mt-8">{{ t('2. Enter the code from Google Authenticator') }}</div>
          </div>
          <div v-else class="two-factor-text mb-14">
            {{ t('Provide your authentication code in the field below to complite sign in.') }}
          </div>

          <ui-input
            class="ui-input__login"
            autofocus
            :label="codeFromGoogleTitle"
            :placeholder="t('Enter your code')"
            v-model="form.token"
            name="token"
            append-inner-icon="fas fa-key"
            :error-messages="tokenErrors"
            @blur="v$.token.$touch()"
            @input="handleTokenInput"
            @keyup.enter="submit"
          />

          <div class="w100">
            <ui-checkbox
              v-model="form.trust"
              class="mb-8"
              :label="t('Trust this computer')"
              :color="$colors.blue"
            />
          </div>

          <ui-button
            button-style="contained"
            :loading="apiLoading"
            :disabled="v$.$invalid"
            iconAppend="fas fa-arrow-right"
            @click="submit"
            class="w100"
            :text="t('Check code')"
          >
          </ui-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import moment from 'moment-timezone';
import { useColors, useI18n } from '@/composables';
import { useStore } from '@/store';
import { $emitter } from '@/services/emitter';
import { loadProfileData } from '@/services/api/api-services/load-profile';
import { twoFactorApiRequest } from '@/services/api/auth/auth-two-factor-api';
import type { TwoFactorForm } from '@/services/api/auth/auth-two-factor-api';
import { getDashboardLink } from '@/utils/get-dashboard-link';

const { t } = useI18n();
const $colors = useColors();
const router = useRouter();
const store = useStore();

const twoFactorQrCode = computed(() => store.member?.twoFactorQrCode);

const rightSideTitle = computed(() =>
  twoFactorQrCode.value ? t('Two-Factor Authentication') : t('Second authentication'),
);

const codeFromGoogleTitle = computed(() =>
  twoFactorQrCode.value ? t('Code From Google Authenticator') : t('Code From App or Backup Code'),
);

const form = reactive({
  token: '',
  trust: true,
});

const isTokenValid = ref(true);

const rules = computed(() => ({
  token: {
    required,
    valid: () => isTokenValid.value,
  },
}));

const v$ = useVuelidate(rules, form);

const apiLoading = ref(false);
const apiErrors = ref('');

const invalidateToken = () => {
  isTokenValid.value = false;
  v$.value.token.$touch();
};

const openBackupCodeDialog = (onClose: () => void) => {
  $emitter.emit('open-dialog', { name: 'TwoFactorBackupCodeDialog', data: { onClose } });
};

const finishAuth = async () => {
  try {
    await loadProfileData();
    const store = useStore();
    await router.push(getDashboardLink(store.user));
  } catch {
    await router.replace('/logout');
  }
};

const apiSubmit = async (payload: TwoFactorForm) => {
  apiErrors.value = '';
  apiLoading.value = true;

  try {
    const result = await twoFactorApiRequest(payload);
    if (result.result) {
      if (result.backupCodeUsed) {
        openBackupCodeDialog(() => {
          void finishAuth();
        });
      } else {
        await finishAuth();
      }
    } else {
      invalidateToken();
    }
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 400 || status === 401 || status === 403) {
      apiErrors.value = t('Wrong email or password');
    }
  } finally {
    apiLoading.value = false;
  }
};

const tokenErrors = computed(() => {
  if (!v$.value.token.$dirty) return [];
  if (v$.value.token.required.$invalid) return [t('Field "Code" is required')];
  if (v$.value.token.valid.$invalid) return [t('Invalid code')];
  return [];
});

const handleTokenInput = () => {
  isTokenValid.value = true;
};

const submit = async () => {
  const valid = await v$.value.$validate();
  if (!valid) return;
  await apiSubmit(form);
};
</script>

<style lang="scss" scoped>
.two-factor-text {
  font-size: 13px;
  color: var(--color-graphite) !important;
  line-height: 1.69;
  letter-spacing: normal;
  text-align: center;
  margin-top: -16px;
  padding: 0 40px;
  margin-bottom: 32px !important;
}

.form-apps {
  display: flex;
}

.form-apps-img {
  height: 38px;
  display: block;
}

.form-qr-code {
  width: 212px;
  margin: 16px auto 0;
}
</style>
